import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { login, getProfile, refreshToken, isLoggedIn } from './authService';
import { RootState } from '../../store';
import { clearAllSessionStorage } from '../../../services/sessionStorage';

interface UserProfile {
    id: number | undefined;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    is_superuser: boolean;
    is_staff: boolean;
    is_active: boolean;
    date_joined: string;
    last_login: string;
    user_type: string;
    organisation: number | undefined;
    result_filters: any[];
    feedback_filters: any[];
    user_filters: any[];
}

interface AuthState {
    access: string | null;
    isAuthenticated: boolean;
    user: UserProfile | null;
    loading: boolean;
    error: string | null;
    isSuperUser: boolean;
    isNovarumUser: boolean;
    isClientUser: boolean;
    organisation: string | null;
}

interface LoginPayload {
    username: string;
    password: string;
}

interface LoginResponse {
    access_token: string;
}

const initialState: AuthState = {
    access: null,
    isAuthenticated: false,
    user: null,
    loading: false,
    error: null,
    isSuperUser: false,
    isNovarumUser: false,
    isClientUser: false,
    organisation: null,
};

// Login User and save access and refresh tokens in auth state
export const loginUser = createAsyncThunk<LoginResponse, LoginPayload, { state: RootState }>(
    'auth/loginUser',
    async ({ username, password }, { dispatch, rejectWithValue }) => {
        try {
            const data = await login(username, password);
            const { access_token } = data;

            dispatch(setCredentials({ access_token }));
            await dispatch(fetchUserProfile()).unwrap();

            return { access_token };
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Unknown error during login');
        }
    }
);

// Handles refresh token and saves new access and refresh tokens in auth state
export const refreshAccessToken = createAsyncThunk<void, void, { state: RootState }>(
    'auth/refreshAccessToken',
    async (_, { getState, dispatch, rejectWithValue }) => {

        const state = getState() as RootState;
        if (!state.auth.access) {
            return rejectWithValue('No access token available');
        }
        if (!isLoggedIn(state.auth.access)) {
            try {
                const data = await refreshToken();
                if (data.access_token) {
                    dispatch(setCredentials({ access_token: data.access_token }));
                    await dispatch(fetchUserProfile()).unwrap();
                } else {
                    dispatch(logout());  // Logout if refresh failed
                }
            } catch (error) {
                console.error("Refresh token failed:", error);
                dispatch(logout());  // Logout if there is an error during refresh
                return rejectWithValue(error);
            }
        }
    }
);

// Handles adding profile information to auth state.  Happens during LoginUser above
export const fetchUserProfile = createAsyncThunk<UserProfile, void, { state: RootState }>(
  'auth/fetchUserProfile',
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const access = state.auth.access;

    if (access) {
      try {
        const userProfile = await getProfile(access);
        return userProfile;
      } catch (error: any) {
        dispatch(logout());  // Logout if fetching the profile fails
        return rejectWithValue(error.response?.data || 'Unknown error fetching user profile');
      }
    } else {
      dispatch(logout());  // Logout if access token is not found
      return rejectWithValue('Access token not found');
    }
  }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCredentials: (state, action: PayloadAction<{ access_token: string}>) => {
            state.access = action.payload.access_token;
            state.isAuthenticated = true;
        },
        logout: (state) => {
            localStorage.clear();
            clearAllSessionStorage();
            Object.assign(state, initialState);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.error = action.payload as string | null;
                state.loading = false;
            })
            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.user = action.payload;
                state.isAuthenticated = true;
                state.isSuperUser = action.payload.is_superuser;
                state.isNovarumUser = action.payload.user_type === "NOVARUM_USER";
                state.isClientUser = action.payload.user_type === "CLIENT_USER";
                state.organisation = action.payload.organisation?.toString() ?? null;
            })
            .addCase(fetchUserProfile.rejected, (state, action) => {
                state.error = action.payload as string | null;
            })
            .addCase(refreshAccessToken.fulfilled, (state) => {
                // Handle successful token refresh if needed
            })
            .addCase(refreshAccessToken.rejected, (state, action) => {
                // Handle failed token refresh if needed
            });
    },
});

export const { setCredentials, logout } = authSlice.actions;
export default authSlice.reducer;

// SNIPPETS TO USE

// FETCH AUTH DATA
// const dispatch = useDispatch<AppDispatch>();
// const authData = useSelector((state: RootState) => state.auth); // Accessing the auth state
// console.log(authData.access)
// console.log(authData.user.id)

// LOGOUT
// const dispatch = useDispatch<AppDispatch>();
//     const handleLogout = () => {
//     dispatch(logout());
// };

// LOGIN
// const dispatch = useDispatch<AppDispatch>();
// const resultAction = await dispatch(loginUser({ username, password }));
// unwrapResult(resultAction); // If the promise is rejected, this will throw and you wont proceed

// AUTHENTICATION CHECK - No longer Needed
// const dispatch = useDispatch<AppDispatch>();
// useEffect(() => {
//   if (!authData.isAuthenticated) {
//       navigate(loginpage);
//       return;
//   }
//   if (!isLoggedIn()) {
//       dispatch(refreshAccessToken());
//   }
// }, [dispatch, navigate, authData.isAuthenticated]);